import { Link } from 'react-router-dom'
import { ChanceGauge } from './ChanceGauge'
import { ClockIcon, TeamIcon } from './icons'
import { OutcomeRow } from './OutcomeRow'
import { useCountdown } from '@/hooks/use-countdown'
import { formatP2pPrice, formatP2pVolume, getP2pMarketLogo, getP2pOptionLogo, parseP2pPercent } from '@/lib/p2p'
import { cn } from '@/lib/utils'
import type { P2PMarket } from '@/types/p2p'

interface P2pCardProps {
	market: P2PMarket
	className?: string
}

export function P2pCard({ market, className }: P2pCardProps) {
	const isSingleOption = market.options.length === 1
	const primaryOption = market.options[0]
	const timeLeft = useCountdown(market.expire_at, 'full')
	const marketLogo = getP2pMarketLogo(market)
	const volume = formatP2pVolume(market.total_volume)

	return (
		<Link
			to={`/p2p-legends/${market.id}`}
			className={cn(
				'group relative flex h-[224px] flex-col gap-2 overflow-hidden rounded-2xl border border-border bg-secondary p-2 pb-2 pt-3 shadow-[0_4px_12px_0_hsl(var(--background)/0.2)] transition-shadow duration-200 hover:ring-[1.5px] hover:ring-primary',
				'[clip-path:polygon(0%_0%,100%_0%,100%_100%,65%_100%,60%_95%,40%_95%,35%_100%,0%_100%)]',
				className,
			)}
		>
			<div className='flex items-start gap-3 px-2'>
				<div className='flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-purple/40 p-0.5'>
					<img src={marketLogo} alt='' className='h-8 w-8 rounded-md object-cover' loading='lazy' />
				</div>
				<div className='min-w-0 flex-1'>
					<h3 className='line-clamp-2 text-sm font-medium tracking-[-0.21px] text-foreground'>{market.title}</h3>
					{market.category ? <p className='mt-0.5 line-clamp-1 text-xs text-muted-foreground'>{market.category}</p> : null}
				</div>
				{isSingleOption && primaryOption ? <ChanceGauge chance={Math.round(parseP2pPercent(primaryOption.percent))} /> : null}
			</div>

			<div className='h-24 shrink-0 overflow-hidden rounded-xl bg-background p-2'>
				{isSingleOption && primaryOption ? (
					<>
						<OutcomeRow
							icon={<TeamIcon src={getP2pOptionLogo(primaryOption, market)} alt={primaryOption.name} />}
							name={primaryOption.name}
							percent={parseP2pPercent(primaryOption.percent)}
							price={formatP2pPrice(primaryOption.price)}
						/>
						<div className='flex items-center gap-1'>
							<span className='relative flex-1 overflow-hidden rounded-lg border border-info bg-background py-2.5 text-center text-sm font-medium tracking-[-0.21px] text-info'>
								<span className='pointer-events-none absolute right-2 top-1/2 h-8 w-8 -translate-y-1/2 rounded-full bg-info opacity-60 blur-xl' />
								<span className='relative'>Yes</span>
							</span>
							<span className='relative flex-1 overflow-hidden rounded-lg border border-warning bg-background py-2.5 text-center text-sm font-medium tracking-[-0.21px] text-warning'>
								<span className='pointer-events-none absolute left-2 top-1/2 h-8 w-8 -translate-y-1/2 rounded-full bg-warning opacity-60 blur-xl' />
								<span className='relative'>No</span>
							</span>
						</div>
					</>
				) : (
					<div className='flex h-full flex-col gap-1 overflow-y-auto'>
						{market.options.map((option) => (
							<OutcomeRow
								key={option.id}
								icon={<TeamIcon src={getP2pOptionLogo(option, market)} alt={option.name} />}
								name={option.name}
								percent={parseP2pPercent(option.percent)}
								price={formatP2pPrice(option.price)}
							/>
						))}
					</div>
				)}
			</div>

			<div className='flex items-center justify-between px-2 pb-1'>
				<span className='text-xs font-medium tracking-[-0.18px] text-muted-foreground'>{volume}</span>
				<span className='flex items-center gap-1.5 text-xs font-medium tracking-[-0.18px] text-muted-foreground'>
					<ClockIcon className='h-4 w-4 text-subtle-foreground' />
					{timeLeft}
				</span>
			</div>

			<span className='pointer-events-none absolute bottom-1.5 left-1/2 h-1 w-10 -translate-x-1/2 rounded-full bg-border' />
		</Link>
	)
}
